// Daily-canonical planner model for the new Plan Edit UI.
//
// Canonical: daily values (sales_plan $, markdowns $, and inventory) are the source of truth.
// Weekly/monthly views are derived and edits mutate the daily draft.

import { applyWeightedPeriodTotalEdit } from './PlannerMonthlyWeights'

export const safeJsonParseArray = (s) => {
  if (Array.isArray(s)) return s
  if (!s) return []
  try {
    const v = JSON.parse(s)
    return Array.isArray(v) ? v : []
  } catch (e) {
    return []
  }
}

export const summarizeDailyPlanValues = (dailyValues) => {
  const rows = safeJsonParseArray(dailyValues)

  return rows.reduce((acc, d) => {
    const salesPlan = Number(d?.sales_plan ?? d?.salesPlan ?? d?.sales ?? 0)
    const markdownPlan = Number(d?.markdowns ?? d?.markdown_plan ?? d?.markdownPlan ?? d?.markdown ?? 0)
    const inventory = Number(d?.inventory ?? d?.inv ?? d?.inventory_bom ?? d?.inventoryBom ?? 0)

    acc.rowCount += 1
    acc.salesPlanTotal += salesPlan
    acc.markdownPlanTotal += markdownPlan
    acc.maxInventory = Math.max(acc.maxInventory, inventory)
    acc.hasAnyNonZero = acc.hasAnyNonZero || salesPlan !== 0 || markdownPlan !== 0 || inventory !== 0
    return acc
  }, {
    rowCount: 0,
    salesPlanTotal: 0,
    markdownPlanTotal: 0,
    maxInventory: 0,
    hasAnyNonZero: false,
  })
}

export const buildDailyIndex = (dailyValues) => {
  // dailyValues are expected to include: date (YYYY-MM-DD), epoch_week, epoch_month
  const byDate = new Map()
  const datesByWeek = new Map()
  const datesByMonth = new Map()

  ;(dailyValues || []).forEach((d) => {
    const date = String(d?.date || '')
    if (!date) return

    const epochWeek = Number(d?.epoch_week)
    const epochMonth = Number(d?.epoch_month)

    // The BQ `daily_values` JSON has evolved; accept multiple field names.
    const salesPlan = Number(
      d?.sales_plan ??
      d?.salesPlan ??
      d?.sales ??
      0
    )

    const markdownPlan = Number(
      d?.markdowns ??
      d?.markdown_plan ??
      d?.markdownPlan ??
      d?.markdowns_plan ??
      d?.markdown ??
      0
    )

    const inventory = Number(
      d?.inventory ??
      d?.inv ??
      d?.inventory_bom ??
      d?.inventoryBom ??
      0
    )

    byDate.set(date, {
      date,
      epochWeek: Number.isFinite(epochWeek) ? epochWeek : null,
      epochMonth: Number.isFinite(epochMonth) ? epochMonth : null,
      // Canonical fields ($)
      salesPlan,
      markdownPlan,
      inventory,
      // Keep originals for debugging/restore
      raw: d,
    })

    if (Number.isFinite(epochWeek)) {
      const key = String(epochWeek)
      const arr = datesByWeek.get(key) || []
      arr.push(date)
      datesByWeek.set(key, arr)
    }

    if (Number.isFinite(epochMonth)) {
      const key = String(epochMonth)
      const arr = datesByMonth.get(key) || []
      arr.push(date)
      datesByMonth.set(key, arr)
    }
  })

  // Sort dates within each bucket to keep stable deterministic behavior.
  for (const [k, arr] of datesByWeek.entries()) {
    arr.sort()
    datesByWeek.set(k, arr)
  }
  for (const [k, arr] of datesByMonth.entries()) {
    arr.sort()
    datesByMonth.set(k, arr)
  }

  return { byDate, datesByWeek, datesByMonth }
}

export const rollupDailyToWeeks = ({ byDate, epochWeeks }) => {
  const out = {}
  ;(epochWeeks || []).forEach((ew) => {
    const key = String(ew)
    out[key] = { salesPlan: 0, markdownPlan: 0 }
  })

  for (const d of byDate.values()) {
    if (!d?.epochWeek) continue
    const key = String(d.epochWeek)
    if (!out[key]) out[key] = { salesPlan: 0, markdownPlan: 0 }
    out[key].salesPlan += Number(d.salesPlan || 0)
    out[key].markdownPlan += Number(d.markdownPlan || 0)
  }

  return out
}

export const rollupDailyToMonths = ({ byDate, epochMonths }) => {
  const out = {}
  ;(epochMonths || []).forEach((em) => {
    const key = String(em)
    out[key] = { salesPlan: 0, markdownPlan: 0 }
  })

  for (const d of byDate.values()) {
    if (!d?.epochMonth) continue
    const key = String(d.epochMonth)
    if (!out[key]) out[key] = { salesPlan: 0, markdownPlan: 0 }
    out[key].salesPlan += Number(d.salesPlan || 0)
    out[key].markdownPlan += Number(d.markdownPlan || 0)
  }

  return out
}

const sum = (arr) => (arr || []).reduce((a, b) => a + Number(b || 0), 0)

export const getTotalForDates = ({ byDate, dates, field }) => {
  const list = Array.isArray(dates) ? dates : []
  return list.reduce((acc, dt) => acc + Number(byDate.get(dt)?.[field] || 0), 0)
}

export const getAnnualTotalByMonths = ({ byDate, datesByMonth, epochMonths, field }) => {
  const months = Array.isArray(epochMonths) ? epochMonths : []
  return months.reduce((acc, em) => {
    const dates = datesByMonth?.get?.(String(em)) || []
    return acc + getTotalForDates({ byDate, dates, field })
  }, 0)
}

const applyDeltaProportional = ({ byDate, dates, field, delta, clampMin = 0 }) => {
  // Apply delta across dates proportionally to their current values; if all 0, split evenly.
  return distributeDeltaAcrossDates({ byDate, dates, field, delta, clampMin })
}

export const applyEditAggregate = ({ byDate, dates, field, newTotal, clampMin = 0 }) => {
  return applyPeriodTotalEdit({ byDate, dates, field, newTotal, clampMin })
}

export const applyEditAllocateScopeLocked = ({
  byDate,
  datesEdited,
  datesToOffset,
  field,
  newTotal,
  clampMin = 0,
}) => {
  const edited = Array.isArray(datesEdited) ? datesEdited : []
  const offset = Array.isArray(datesToOffset) ? datesToOffset : []
  if (!edited.length) return byDate

  const curEdited = getTotalForDates({ byDate, dates: edited, field })
  const delta = Number(newTotal || 0) - Number(curEdited || 0)
  let next = applyDeltaProportional({ byDate, dates: edited, field, delta, clampMin })

  if (!offset.length || delta === 0) return next

  next = applyDeltaProportional({ byDate: next, dates: offset, field, delta: -delta, clampMin })
  return next
}

export const applyEditAllocateAnnualLocked = ({
  byDate,
  // edited period
  datesEdited,
  field,
  newTotal,
  // allocation scope
  epochMonths,
  datesByMonth,
  editedEpochMonths = [],
  clampMin = 0,
}) => {
  // Offset the delta out of the OTHER months in the active annual window.
  // This matches the planner's allocate-mode expectation for weekly edits:
  // the edited month changes, and the rest of the annual window absorbs the offset.
  const editedMonthsSet = new Set((editedEpochMonths || []).map(String))
  const annualMonths = Array.isArray(epochMonths) && epochMonths.length
    ? epochMonths.map(String)
    : Array.from(datesByMonth?.keys?.() || []).map(String)

  const otherDates = []
  annualMonths.forEach((em) => {
    if (editedMonthsSet.has(String(em))) return
    const ds = datesByMonth?.get?.(String(em)) || []
    ;(ds || []).forEach((dt) => {
      otherDates.push(dt)
    })
  })

  return applyEditAllocateScopeLocked({
    byDate,
    datesEdited,
    datesToOffset: otherDates,
    field,
    newTotal,
    clampMin,
  })
}

export const applyEditAllocateMonthLocked = ({
  byDate,
  datesEdited,
  field,
  newTotal,
  datesByMonth,
  editedEpochMonths = [],
  clampMin = 0,
}) => {
  const editedDates = Array.isArray(datesEdited) ? datesEdited : []
  if (!editedDates.length) return byDate

  const editedSet = new Set(editedDates.map(String))
  const explicitMonthKeys = (Array.isArray(editedEpochMonths) ? editedEpochMonths : [])
    .map((value) => String(value || ''))
    .filter(Boolean)

  const inferredMonthKeys = explicitMonthKeys.length
    ? explicitMonthKeys
    : Array.from(new Set(
      editedDates
        .map((dt) => String(byDate?.get?.(dt)?.epochMonth || ''))
        .filter(Boolean)
    ))

  const nextAfterEdit = applyPeriodTotalEdit({
    byDate,
    dates: editedDates,
    field,
    newTotal,
    clampMin,
  })

  if (!inferredMonthKeys.length || !datesByMonth?.get) return nextAfterEdit

  let next = nextAfterEdit

  inferredMonthKeys.forEach((monthKey) => {
    const monthDates = datesByMonth.get(String(monthKey)) || []
    if (!monthDates.length) return

    const editedMonthDates = monthDates.filter((dt) => editedSet.has(String(dt)))
    const offsetDates = monthDates.filter((dt) => !editedSet.has(String(dt)))
    if (!editedMonthDates.length || !offsetDates.length) return

    const beforeEditedMonthTotal = getTotalForDates({ byDate, dates: editedMonthDates, field })
    const afterEditedMonthTotal = getTotalForDates({ byDate: nextAfterEdit, dates: editedMonthDates, field })
    const monthDelta = Number(afterEditedMonthTotal || 0) - Number(beforeEditedMonthTotal || 0)
    if (Math.abs(monthDelta) <= 0.000001) return

    next = applyDeltaProportional({
      byDate: next,
      dates: offsetDates,
      field,
      delta: -monthDelta,
      clampMin,
    })
  })

  return next
}

export const distributeDeltaAcrossDates = ({ byDate, dates, field, delta, clampMin = 0 }) => {
  const list = Array.isArray(dates) ? dates : []
  if (!list.length) return byDate

  // Weight by current values; if all zero, weight evenly.
  const curVals = list.map((dt) => Number(byDate.get(dt)?.[field] || 0))
  const total = sum(curVals)
  const weights = total > 0 ? curVals.map((v) => v / total) : list.map(() => 1 / list.length)

  const next = new Map(byDate)
  list.forEach((dt, idx) => {
    const rec = next.get(dt)
    if (!rec) return
    const add = delta * weights[idx]
    const raw = Number(rec[field] || 0) + add
    const clamped = raw < clampMin ? clampMin : raw
    next.set(dt, { ...rec, [field]: clamped })
  })

  return next
}

export const applyPeriodTotalEdit = ({ byDate, dates, field, newTotal, clampMin = 0 }) => {
  const list = Array.isArray(dates) ? dates : []
  if (!list.length) return byDate

  const weighted = applyWeightedPeriodTotalEdit({ byDate, dates: list, field, newTotal, clampMin })
  if (weighted !== byDate) return weighted

  const curTotal = list.reduce((acc, dt) => acc + Number(byDate.get(dt)?.[field] || 0), 0)
  const delta = Number(newTotal || 0) - Number(curTotal || 0)
  return distributeDeltaAcrossDates({ byDate, dates: list, field, delta, clampMin })
}
